import React, { useReducer, useEffect, createContext, useContext, useState } from 'react'
import api from '@api'

import {
  IFloorMap,
  FloorMapSpaces,
  FloorMapPins,
  IFloorMapSpace,
  IUsageType,
  IFullEmployee,
  IViewFiltersData,
  RawViewFilters,
} from '@api/types'
import { useUser, useUsageTypes } from '@hooks'
import useSettings from 'components/NewSettings/hooks/useSettings'
import { useLocationBookableDesks } from 'components/NewSettings/hooks/useLocationBookableDesks'
import useLocations from '@ui/selects/LocationSelect/useLocations'
import { Mode } from '@ui/selects/LocationSelect'
import useSelect from '@ui/selects/LocationSelect/useSelection'
import { navigateWithBasepath as navigate } from '../../../App'
import useSpaceTypes from 'components/SpaceTypeManagement/hooks/useSpaceTypes'
import { getUsageTypesByLocations, isAnyAdminRole, isManagedByUser, isSeatable } from '@utils'
import { meetingRoom } from '@ui/canvas/utils'
import { useSelection, UseSelectionHook } from './hooks'
import { get } from 'lodash'
import { formatDate2, getStartAndEndDate, parseDate2 } from '@utils'
import { useBookingDesk } from '@providers/bookingDeskProvider'
import { DateUtil, TIME } from '@utils/dateUtil'
import { useAppData } from '@providers/appDataProvider'
import useLocalStorage from 'use-local-storage'

export interface UseFloorHook extends IFloorState {
  selection?: UseSelectionHook
  setApId?: (apId: string) => void
  setFloorId?: (floorId: string) => void
  setIsBookDeskPage?: (floorId: string | null) => void
  setIsBookMeetingRoomPage?: (floorId: string | null) => void
  updateSpaces?: (spaces: IFloorMapSpace[]) => void
  updateSpace?: (space: any) => void
  init?: ({ floorId, apId }: { floorId: string; apId: string }) => void
  spaceLocate?: ({ floorId, spaceId }: { floorId: string; spaceId: string }) => void
  reloadMap?: (fromDate?: DateUtil, toDate?: DateUtil | null) => void
  setConfirm?: ({
    employee,
    space,
    nextFromSpace,
    nextToSpace,
  }: {
    employee: IFullEmployee
    space: any
    nextFromSpace: IFloorMapSpace | null
    nextToSpace: IFloorMapSpace
  }) => void
  clearConfirm?: () => void
  fetchSpaces?: () => void
  viewFilters?: RawViewFilters
}

enum Actions {
  INIT,
  AP_CHANGE,
  FLOOR_CHANGE,
  MAP_LOAD,
  SPACES_LOAD,
  SPACE_ASSIGNMENTS_LOAD,
  PINS_LOAD,
  SPACES_UPDATE,
  SPACE_UPDATE, // for space dialog updates
  IS_BOOK_DESK_PAGE,
  IS_BOOK_MEETING_ROOM_PAGE,
  SPACE_LOCATE,
  CONFIRM_BOX,
  CLEAR_CONFIRM_BOX,
  PRINTABLE_QR_CODE_USAGE_TYPES,
  VIEW_FILTERS,
}

interface Init {
  type: Actions.INIT
  apId: string
  floorId: string
  reqId: string
}

interface APChange {
  type: Actions.AP_CHANGE
  apId: string
  reqId: string
}

interface FloorChange {
  type: Actions.FLOOR_CHANGE
  floorId: string | null
  reqId: string
}

interface MapLoad {
  type: Actions.MAP_LOAD
  map: IFloorMap
  reqId: string
}

interface SpacesLoad {
  type: Actions.SPACES_LOAD
  spaces: FloorMapSpaces
  reqId: string
}

interface SpaceAssignmentsLoad {
  type: Actions.SPACE_ASSIGNMENTS_LOAD
  spaces: FloorMapSpaces
  reqId: string
}

interface PinsLoad {
  type: Actions.PINS_LOAD
  pins: FloorMapPins
  reqId: string
}

interface SpacesUpdate {
  type: Actions.SPACES_UPDATE
  spaces: IFloorMapSpace[]
  reqId: string
}

// for space dialog updates
interface SpaceUpdate {
  type: Actions.SPACE_UPDATE
  space: IFloorMapSpace
  reqId: string
}

export interface IFloorState {
  apId: string
  floorId: string | null
  map: IFloorMap | null
  progress: number
  reqId: string
  spaces: FloorMapSpaces
  pins: FloorMapPins
  isBookDeskPage: boolean
  isBookMeetingRoomPage: boolean
  bookingWorkdayTimeFrom: string
  bookingWorkdayTimeTo: string
  printableQrCodeUsageTypes: string[]
}

interface IsBookDeskPage {
  type: Actions.IS_BOOK_DESK_PAGE
  isBookDeskPage: boolean
  reqId: string
}

interface PrintableQrCodeUsageTypes {
  type: Actions.PRINTABLE_QR_CODE_USAGE_TYPES
  printableQrCodeUsageTypes: string[]
  reqId: string
}

interface IsBookMeetingRoomPage {
  type: Actions.IS_BOOK_MEETING_ROOM_PAGE
  isBookMeetingRoomPage: boolean
  reqId: string
}

interface SpaceLocate {
  type: Actions.SPACE_LOCATE
  locate: { spaceId: string; floorId: string }
  reqId: string
}

interface Confirm {
  type: Actions.CONFIRM_BOX
  confirm: {
    employee: IFullEmployee
    space: any
    nextFromSpace: IFloorMapSpace | null
    nextToSpace: IFloorMapSpace
  }
  reqId: string
}

interface ClearConfirm {
  type: Actions.CLEAR_CONFIRM_BOX
  reqId: string
}

interface ViewFilters {
  type: Actions.VIEW_FILTERS
  reqId: string
  viewFilters: RawViewFilters
}

type RootAction =
  | Init
  | APChange
  | FloorChange
  | MapLoad
  | SpacesLoad
  | SpaceAssignmentsLoad
  | PinsLoad
  | SpacesUpdate
  | SpaceUpdate
  | IsBookDeskPage
  | IsBookMeetingRoomPage
  | SpaceLocate
  | Confirm
  | ClearConfirm
  | PrintableQrCodeUsageTypes
  | ViewFilters

function reducer(state: IFloorState, action: RootAction) {
  // Ignore outdated requests
  if (
    ![Actions.AP_CHANGE, Actions.FLOOR_CHANGE, Actions.INIT].includes(action.type) &&
    action.reqId !== state.reqId
  ) {
    return state
  }

  switch (action.type) {
    case Actions.INIT:
      return {
        ...state,
        apId: action.apId,
        floorId: action.floorId,
        progress: 60,
        reqId: action.reqId,
      }

    case Actions.AP_CHANGE:
      return {
        ...state,
        apId: action.apId,
        progress: 60,
        reqId: action.reqId,
      }

    case Actions.FLOOR_CHANGE: {
      return {
        ...state,
        floorId: action.floorId,
        map: null,
        progress: 0,
        reqId: action.reqId,
        spaces: { ids: [], byId: {} },
      }
    }

    case Actions.MAP_LOAD:
      return {
        ...state,
        map: action.map,
        progress: state.progress + 20,
      }

    case Actions.SPACES_LOAD:
      return {
        ...state,
        progress: Math.min(state.progress + 20, 100),
        spaces: state.spaces.ids.length ? state.spaces : action.spaces,
      }

    case Actions.SPACE_ASSIGNMENTS_LOAD:
      return {
        ...state,
        progress: Math.min(state.progress + 40, 100),
        spaces: action.spaces,
      }

    case Actions.PINS_LOAD:
      return {
        ...state,
        progress: Math.min(state.progress + 20, 100),
        pins: action.pins,
      }

    case Actions.SPACES_UPDATE:
      return {
        ...state,
        spaces: {
          ...state.spaces,
          byId: {
            ...state.spaces.byId,
            ...action.spaces.reduce(
              (result, space) =>
                state.spaces.byId[space.id]
                  ? {
                      ...result,
                      [space.id]: space,
                    }
                  : result,
              {}
            ),
          },
        },
      }

    // for space dialog updates
    case Actions.SPACE_UPDATE:
      return {
        ...state,
        spaces: {
          ...state.spaces,
          byId: {
            ...state.spaces.byId,
            [action.space.id]: {
              ...state.spaces.byId[action.space.id],
              ...action.space,
            },
          },
        },
      }
    case Actions.IS_BOOK_DESK_PAGE:
      return {
        ...state,
        isBookDeskPage: action.isBookDeskPage,
      }

    case Actions.IS_BOOK_MEETING_ROOM_PAGE:
      return {
        ...state,
        isBookMeetingRoomPage: action.isBookMeetingRoomPage,
      }
    case Actions.SPACE_LOCATE:
      return {
        ...state,
        locateSpace: action.locate,
      }

    case Actions.CONFIRM_BOX:
      return {
        ...state,
        confirm: action.confirm,
      }

    case Actions.CLEAR_CONFIRM_BOX:
      return {
        ...state,
        confirm: null,
      }

    case Actions.PRINTABLE_QR_CODE_USAGE_TYPES:
      return {
        ...state,
        printableQrCodeUsageTypes: action.printableQrCodeUsageTypes,
      }

    case Actions.VIEW_FILTERS:
      return { ...state, viewFilters: action.viewFilters }

    default:
      return state
  }
}

const initValues = {
  apId: '',
  floorId: '',
  map: null,
  progress: 0,
  reqId: '-',
  spaces: { ids: [], byId: {} },
  pins: { ids: [], byId: {} },
  isBookDeskPage: false,
  isBookMeetingRoomPage: false,
  locateSpace: null,
  bookingWorkdayTimeFrom: '0:0',
  bookingWorkdayTimeTo: '23:59',
  confirm: null,
  printableQrCodeUsageTypes: [],
}

const UseFloorContext = createContext<UseFloorHook>(initValues)

export const UseFloorProvider = ({ children }: { children: React.ReactNode }) => {
  const floor = useFloorHook()

  return <UseFloorContext.Provider value={floor}>{children}</UseFloorContext.Provider>
}

const useFloor = () => useContext(UseFloorContext)

function useFloorHook(initApId = '', initFloorId = ''): UseFloorHook {
  const [state, dispatch] = useReducer(reducer, {
    ...initValues,
    apId: initApId,
    isBookDeskPage: false,
    isBookMeetingRoomPage: false,
    floorId: initFloorId,
    reqId: `${initFloorId}-${initApId}`,
  })

  const primaryPlanId = useAppData().primaryAssignmentPlan.id
  const selection = useSelection()
  const { ...hook } = useSettings()
  const user = useUser()
  const usageTypesList = useUsageTypes()
  const regions = useLocations()
  const { bookableDesks } = useLocationBookableDesks()
  const {
    types: { spaceType },
  } = useAppData()
  const value = useSelect(Mode.SINGLE_FLOOR, state.floorId, regions)
  const bookingWorkdayTimeFrom = get(value, 'bookingWorkdayTimeFrom') || hook.bookingWorkdayTimeFrom
  const bookingWorkdayTimeTo = get(value, 'bookingWorkdayTimeTo') || hook.bookingWorkdayTimeTo
  const {
    date: { fromDate, toDate: lastDate },
  } = useBookingDesk()
  const [, setLocalStorageItem] = useLocalStorage('locate-space', '')

  const toDate = lastDate || fromDate

  const [currentFromDate, setCurrentFromDate] = useState(fromDate)
  const [currentToDate, setCurrentToDate] = useState(toDate)

  // Update state when fromDate and toDate change
  useEffect(() => {
    setCurrentFromDate(fromDate)
    setCurrentToDate(toDate)
  }, [fromDate, toDate])

  function onFloorChange() {
    if (state.floorId && state.apId) {
      const reqId = `${state.floorId}-${state.apId}`
      loadMap(state.floorId, reqId)
    }
  }

  const loadMap = (floorId: string, reqId: string) => {
    api.floor.map(floorId).then((map) => {
      dispatch({ type: Actions.MAP_LOAD, map, reqId })
      reloadMap(currentFromDate, currentToDate)
    })
  }

  const reloadMap = (updatedFromDate?: DateUtil, updatedToDate?: DateUtil | null) => {
    const reqId = `${state.floorId}-${state.apId}`
    if (state.isBookDeskPage) {
      fetchBookableDesk(updatedFromDate, updatedToDate)
    } else if (state.isBookMeetingRoomPage) {
      fetchBookableMeetingRooms(updatedFromDate, updatedToDate)
    } else {
      if (state.floorId) {
        api.floor
          .spaces(state.floorId)
          .then(
            ({
              spaces,
              printableQrCodeUsageTypes,
            }: {
              spaces: FloorMapSpaces
              printableQrCodeUsageTypes: string[]
            }) => {
              dispatch({ type: Actions.SPACES_LOAD, spaces, reqId })
              dispatch({
                type: Actions.PRINTABLE_QR_CODE_USAGE_TYPES,
                printableQrCodeUsageTypes,
                reqId,
              })
            }
          )
        api.floor.spaceAssignments(state.floorId, state.apId).then(({ spaces, viewFilters }) => {
          dispatch({ type: Actions.SPACE_ASSIGNMENTS_LOAD, spaces, reqId })
          dispatch({ type: Actions.VIEW_FILTERS, viewFilters, reqId })
        })

        api.floor.pins(state.floorId).then((pins: FloorMapPins) => {
          dispatch({ type: Actions.PINS_LOAD, pins, reqId })
        })
      }
    }
  }

  const fetchBookableDesk = (updatedFromDate?: DateUtil, updatedToDate?: DateUtil | null) => {
    const reqId = `${state.floorId}-${state.apId}`
    const { city, country, timezone } = value?.building
    const loggedInUser = user?.user

    const locSeats =
      hook.bookableSpaceTypesLocationsOverridesEnabled && bookableDesks && bookableDesks.length
        ? getUsageTypesByLocations(bookableDesks, { state: value?.building?.state, city, country })
        : []
    const seatables = locSeats.length
      ? locSeats
      : get(hook, 'bookableSpaceTypes', ['hoteling_desk'])
    const usageTypeId_in = usageTypesList
      .filter((usageType: IUsageType) => isSeatable({ usageType }, seatables))
      .map((e) => e.id)

    const { fromDate, toDate } = getStartAndEndDate({
      timezone,
      allDay: !hook.hourlyBookingEnabled && hook.dailyBookingEnabled,
      defaultTimeFrom: bookingWorkdayTimeFrom,
      defaultTimeTo: bookingWorkdayTimeTo,
      startDate: DateUtil.tz(timezone),
    })
    const DATE_PARSE_FORMAT = 'yyyy-MM-dd HH:mm:ss'
    api.floor
      .fetchBookingDesks(
        formatDate2(
          updatedFromDate === updatedToDate ? toDate : (updatedToDate as DateUtil),
          DATE_PARSE_FORMAT
        ),
        formatDate2(
          updatedFromDate === updatedToDate ? fromDate : (updatedFromDate as DateUtil),
          DATE_PARSE_FORMAT
        ),
        state.floorId,
        state.apId,
        usageTypeId_in,
        loggedInUser?.employee.id
      )
      .then((resp: any) => {
        const {
          data: {
            viewer: {
              company: {
                spaceConnection: { edges },
              },
            },
          },
        } = resp

        const spaces = edges.reduce(
          (a: any, { node: b }: { node: any; b: any }) => {
            const data: any = []
            b.dailyBookableSpacesAvailability.forEach((b1: any) => {
              b1.bookings.forEach((booking: any) => {
                const bStart = parseDate2(booking.startDate, timezone)
                const bEnd = parseDate2(booking.endDate, timezone)
                if (
                  !(fromDate.isBefore(bStart) && toDate.isSameOrBefore(bStart)) ||
                  !(fromDate.isSameOrAfter(bEnd) && toDate.isAfter(bEnd))
                ) {
                  data.push(booking)
                }
              })
            })
            b.bookings = data
            b.isAvailable = b.dailyBookableSpacesAvailability.every((c: any) => c.availability)
            return ((b.isBookableByEmployee || b.isBookableUsageType) &&
              b.isAvailable &&
              loggedInUser &&
              isManagedByUser(b, loggedInUser, isAnyAdminRole(loggedInUser.role))) ||
              !b.isAvailable
              ? {
                  ...a,
                  raw: [...(a.raw || []), b],
                  byId: { ...(a.byId || {}), [b.id]: b },
                  ids: [...(a.ids || []), b.id],
                }
              : a
          },
          { ids: [] }
        )
        dispatch({ type: Actions.SPACES_LOAD, spaces: spaces, reqId })
        dispatch({ type: Actions.SPACE_ASSIGNMENTS_LOAD, spaces, reqId })
        dispatch({ type: Actions.PINS_LOAD, pins: { ids: [], byId: {} }, reqId })
      })
  }

  const fetchBookableMeetingRooms = (
    updatedFromDate?: DateUtil,
    updatedToDate?: DateUtil | null
  ) => {
    const reqId = `${state.floorId}-${state.apId}`
    const { timezone } = value?.building

    const startDate = DateUtil.tz(timezone).reset(TIME.SECONDS)
    const allDay = !hook.hourlyBookingEnabled
    if (!fromDate || !toDate) {
      const { fromDate, toDate } = getStartAndEndDate({
        startDate,
        timezone,
        defaultTimeFrom: bookingWorkdayTimeFrom,
        defaultTimeTo: bookingWorkdayTimeTo,
        allDay,
      })
    }
    const DATE_PARSE_FORMAT = 'yyyy-MM-dd HH:mm:ss'
    api.floor
      .fetchMeetingRoom(
        formatDate2(updatedToDate ?? toDate, DATE_PARSE_FORMAT),
        formatDate2(updatedFromDate ?? fromDate, DATE_PARSE_FORMAT),
        state.floorId,
        state.apId,
        spaceType.filter((s: any) => meetingRoom(s)).map((s: any) => s.id)
      )
      .then((resp: any) => {
        const {
          data: {
            viewer: {
              company: {
                spaceConnection: { edges },
              },
            },
          },
        } = resp

        const spaces = edges.reduce(
          (a: any, { node: b }: { node: any; b: any }) => {
            const data: any = []
            b.dailyBookableSpacesAvailability.forEach((b1: any) => {
              b1.bookings.forEach((booking: any) => {
                const bStart = parseDate2(booking.startDate, timezone)
                const bEnd = parseDate2(booking.endDate, timezone)
                if (
                  !(fromDate.isBefore(bStart) && toDate.isSameOrBefore(bStart)) ||
                  !(fromDate.isSameOrAfter(bEnd) && toDate.isAfter(bEnd))
                ) {
                  data.push(booking)
                }
              })
            })
            b.bookings = data
            b.isAvailable = b.dailyBookableSpacesAvailability.every((c: any) => c.availability)
            return {
              ...a,
              raw: [...(a.raw || []), b],
              byId: { ...(a.byId || {}), [b.id]: b },
              ids: [...(a.ids || []), b.id],
            }
          },
          { ids: [] }
        )
        dispatch({ type: Actions.SPACES_LOAD, spaces: spaces, reqId })
        dispatch({ type: Actions.SPACE_ASSIGNMENTS_LOAD, spaces, reqId })
        dispatch({ type: Actions.PINS_LOAD, pins: { ids: [], byId: {} }, reqId })
      })
  }

  function onApChange() {
    if (state.floorId && state.apId) {
      const reqId = `${state.floorId}-${state.apId}`

      api.floor.spaceAssignments(state.floorId, state.apId).then(({ spaces, viewFilters }) => {
        dispatch({ type: Actions.SPACE_ASSIGNMENTS_LOAD, spaces, reqId })
        dispatch({ type: Actions.VIEW_FILTERS, viewFilters, reqId })
      })
    }
  }

  const setFloorId = (floorId: string) =>
    dispatch({
      type: Actions.FLOOR_CHANGE,
      floorId,
      reqId: `${floorId}-${state.apId}`,
    })

  const fetchSpaces = () => {
    return state.spaces
  }

  const spaceLocate = (locate: { spaceId: string; floorId: string }) => {
    dispatch({ type: Actions.SPACE_LOCATE, locate, reqId: `${locate.floorId}-${locate.spaceId}` })
    if (locate.floorId !== state.floorId || !window.location.pathname.includes('/floor_plan')) {
      if (window.location.pathname.includes('/floor_plan')) {
        selection.clear()
        selection.add([locate.spaceId])
        setFloorId(locate.floorId)
        onFloorChange()
      } else {
        setLocalStorageItem(locate.spaceId)
        navigate(`/floor_plan/${locate.floorId}`, {})
      }
    } else {
      selection.clear()
      selection.add([locate.spaceId])
    }
  }

  useEffect(onFloorChange, [state.floorId, state.isBookDeskPage, state.isBookMeetingRoomPage])
  useEffect(onApChange, [state.apId])

  return {
    ...state,
    selection,
    bookingWorkdayTimeFrom: bookingWorkdayTimeFrom || hook.bookingWorkdayTimeFrom,
    bookingWorkdayTimeTo: bookingWorkdayTimeTo || hook.bookingWorkdayTimeTo,
    setApId: (apId) =>
      dispatch({
        type: Actions.AP_CHANGE,
        apId,
        reqId: `${state.floorId}-${apId}`,
      }),
    setIsBookDeskPage: (floorId) => {
      if (state.isBookMeetingRoomPage) {
        dispatch({
          type: Actions.IS_BOOK_MEETING_ROOM_PAGE,
          isBookMeetingRoomPage: false,
          reqId: `${floorId}-${state.apId}`,
        })
      }
      dispatch({
        type: Actions.IS_BOOK_DESK_PAGE,
        isBookDeskPage: !state.isBookDeskPage,
        reqId: `${floorId}-${state.apId}`,
      })
    },
    setIsBookMeetingRoomPage: (floorId) => {
      if (state.isBookDeskPage) {
        dispatch({
          type: Actions.IS_BOOK_DESK_PAGE,
          isBookDeskPage: false,
          reqId: `${floorId}-${state.apId}`,
        })
      }
      dispatch({
        type: Actions.IS_BOOK_MEETING_ROOM_PAGE,
        isBookMeetingRoomPage: !state.isBookMeetingRoomPage,
        reqId: `${floorId}-${state.apId}`,
      })
    },
    setFloorId,
    updateSpaces: (spaces) =>
      dispatch({
        type: Actions.SPACES_UPDATE,
        spaces,
        reqId: `${state.floorId}-${state.apId}`,
      }),
    updateSpace: (space) =>
      dispatch({
        type: Actions.SPACE_UPDATE,
        space,
        reqId: `${state.floorId}-${state.apId}`,
      }),
    init: ({ floorId, apId }) => {
      dispatch({
        type: Actions.INIT,
        floorId,
        apId,
        reqId: `${floorId}-${apId}`,
      })
    },
    setConfirm: ({ employee, space, nextFromSpace, nextToSpace }) =>
      dispatch({
        type: Actions.CONFIRM_BOX,
        confirm: { employee, space, nextFromSpace, nextToSpace },
        reqId: `${state.floorId}-${state.apId}`,
      }),
    clearConfirm: () => {
      dispatch({
        type: Actions.CLEAR_CONFIRM_BOX,
        reqId: `${state.floorId}-${state.apId}`,
      })
    },
    fetchSpaces,
    reloadMap,
    spaceLocate,
  }
}

export default useFloor
